import React, { useState, useMemo } from 'react';
import { Button, Dialog, Checkbox } from '@ariakit/react';
import { PinIcon,Eye, EyeOff, Check, GripVertical, Settings2, Pin, PinOff, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Table, Column } from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ColumnVisibilityProps<TData> {
  table: Table<TData>;
  enableUserControl?: boolean;
  enableColumnPinning?: boolean;
  onClose?: () => void;
}

interface SortableColumnItemProps<TData> {
  column: Column<TData>;
  columnName: string;
  table: Table<TData>;
  enableColumnPinning?: boolean;
}

function SortableColumnItem<TData>({ column, columnName, table, enableColumnPinning }: SortableColumnItemProps<TData>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`column-visibility-item-${column.id}`}
      className={`
        flex items-center gap-3 p-2 rounded
        ${isDragging ? 'opacity-50 bg-gray-100' : 'hover:bg-gray-50'}
      `}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-move"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      <Checkbox
        checked={column.getIsVisible()}
        onChange={(e) => column.toggleVisibility(e.target.checked)}
        data-testid={`column-visibility-checkbox-${column.id}`}
        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      />
      <div className="flex items-center gap-2 flex-1">
        {column.getIsVisible() ? (
          <Eye className="w-4 h-4 text-gray-400" />
        ) : (
          <EyeOff className="w-4 h-4 text-gray-400" />
        )}
        <span className="text-sm text-gray-700 select-none">
          {columnName}
        </span>
      </div>
      {enableColumnPinning && (
        <div className="flex items-center border border-gray-300 rounded overflow-hidden">
          {column.getIsPinned() === false ? (
            <>
              <button
                onClick={() => column.pin('left')}
                className="p-1.5 hover:bg-blue-50 hover:text-blue-600 text-gray-400 border-gray-300 transition-colors"
                title="Pin to left"
              >
                <PinIcon className="w-3 h-3" />
              </button>
                          {/* <button
                onClick={() => column.pin('right')}
                className="p-1.5 hover:bg-blue-50 hover:text-blue-600 text-gray-400 transition-colors"
                title="Pin to right"
              >
                <ChevronRight className="w-3 h-3" />
              </button> */}
            </>
          ) : (
            <button
              onClick={() => column.pin(false)}
              className="p-1.5 hover:bg-red-50 hover:text-red-600 text-gray-500 transition-colors border-yellow-400 bg-yellow-200"
              title={`Unpin from ${column.getIsPinned()}`}
            >
              <PinIcon className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

    </div>
  );
}

export default function ColumnVisibility<TData>({ 
  table, 
  enableUserControl = true,
  enableColumnPinning = false,
  onClose 
}: ColumnVisibilityProps<TData>) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const allColumns = table.getAllColumns().filter((column) => 
    typeof column.accessorFn !== 'undefined' || column.id !== 'select'
  );

  const columnOrder = table.getState().columnOrder;
  const currentOrder = columnOrder.length ? columnOrder : allColumns.map(c => c.id);

  const orderedColumns = useMemo(() => {
    const ordered: Column<TData>[] = [];
    currentOrder.forEach(id => {
      const col = allColumns.find(c => c.id === id);
      if (col) ordered.push(col);
    });
    allColumns.forEach(col => {
      if (!currentOrder.includes(col.id)) {
        ordered.push(col);
      }
    });
    return ordered;
  }, [allColumns, currentOrder]);

  const filteredColumns = useMemo(() => {
    if (!searchQuery) return orderedColumns;
    
    return orderedColumns.filter((column) => {
      const header = column.columnDef.header;
      const columnName = typeof header === 'string' 
        ? header 
        : column.id;
      
      return columnName.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [orderedColumns, searchQuery]);

  const visibleCount = allColumns.filter(col => col.getIsVisible()).length;

  const handleToggleAll = (checked: boolean) => {
    filteredColumns.forEach(column => {
      column.toggleVisibility(checked);
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = currentOrder.indexOf(active.id as string);
      const newIndex = currentOrder.indexOf(over.id as string);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
        table.setColumnOrder(newOrder);
      }
    }

    setActiveId(null);
  };

  const allVisible = filteredColumns.every(col => col.getIsVisible());
  const someVisible = filteredColumns.some(col => col.getIsVisible()) && !allVisible;

  const handleClose = () => {
    setOpen(false);
    setSearchQuery('');
    onClose?.(); // Call the onClose callback to save settings
  };

  if (!enableUserControl) return null;

  const activeColumn = activeId ? allColumns.find(c => c.id === activeId) : null;
  const activeColumnName = activeColumn ? (
    typeof activeColumn.columnDef.header === 'string' 
      ? activeColumn.columnDef.header 
      : activeColumn.id
  ) : '';

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <Settings2 className="w-4 h-4" />
        Manage Columns ({visibleCount}/{allColumns.length})
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        backdrop={
          <div className="fixed inset-0 bg-black/50" />
        }
      >
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-row justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Manage Columns
              </h2>
              <span className="text-sm text-gray-500">
                {visibleCount} of {allColumns.length} visible
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Show/hide columns and drag to reorder
            </p>
          </div>

          <div className="px-6 py-3 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search columns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-3">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredColumns.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {filteredColumns.map((column) => {
                    const header = column.columnDef.header;
                    const columnName = typeof header === 'string' 
                      ? header 
                      : column.id;

                    return (
                      <SortableColumnItem
                        key={column.id}
                        column={column}
                        columnName={columnName}
                        table={table}
                        enableColumnPinning={enableColumnPinning}
                      />
                    );
                  })}
                </div>
              </SortableContext>
              
              <DragOverlay>
                {activeId ? (
                  <div className="flex items-center gap-3 p-2 rounded bg-white shadow-lg border border-gray-200">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">
                        {activeColumnName}
                      </span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>

            {filteredColumns.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No columns found matching "{searchQuery}"
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <Button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
